import { IAPIRoutes } from "../../../../config";
import { featureFlag } from "../../../../constants/feature";

import { ITimeProvider } from "../providers/provider";
import {
  formatDaily3DData,
  formatDailyData,
  formatHourly3DData,
  formatMotion,
  mvmtFields,
  processCDLData,
} from "../workers/cdlWorker";
import CDLWS from "./cdl_ws.js";

const instance = await CDLWS();

/* Input: base64-encoded string containing CDL timestamps
 * Output: array of Date elements
 */
export const decode_cdl_timestamps = async (b64Ts: string) => {
  const fetchUrl = "data:application/octet-binary;base64," + b64Ts;
  const rawFetch = await fetch(fetchUrl);
  const rawTs = await rawFetch.arrayBuffer();
  const rawTsU8 = new Uint8Array(rawTs);

  const ptrIn = instance._malloc(rawTsU8.length);
  instance.HEAP8.set(rawTsU8, ptrIn);
  const ptrOut = instance._cdl_decode_ts(ptrIn, rawTsU8.length);
  if (!ptrOut) {
    throw new Error("TS conversion failed.");
  }

  const nTsOut = instance.HEAPF64[ptrOut / Float64Array.BYTES_PER_ELEMENT];

  const outArr = [];
  for (let i = 0; i < nTsOut; i++) {
    outArr.push(
      new Date(
        instance.HEAPF64[ptrOut / Float64Array.BYTES_PER_ELEMENT + i + 1]
      )
    );
  }

  instance._free(ptrIn);
  instance._free(ptrOut);

  return outArr;
};

const rrCDLFields = "rr_3s.ds_hrly,rr_3s.ds_dly";
const bedExitFields = featureFlag.bedExitMotionGraphSettings;
const heartRateFields = `hr_3s.ds_hrly,hr_3s.ds_dly`;
// Per-minute downsamples for the gated Patient Profile "1D" minutely view.
const rrMinutelyField = "rr_3s.ds_min";
const hrMinutelyField = "hr_3s.ds_min";

const vitalsBatchQuery: Record<
  ICDLVitalsMetric,
  Record<ICDLVitalsGranularity, { db: string; field: string }>
> = {
  rr: {
    hourly: { db: "3s", field: "rr_3s.ds_hrly" },
    daily: { db: "3s", field: "rr_3s.ds_dly" },
  },
  hr: {
    hourly: { db: "HR3s", field: "hr_3s.ds_hrly" },
    daily: { db: "HR3s", field: "hr_3s.ds_dly" },
  },
};

const getErrorMessage = (body: Record<string, unknown>) => {
  const message = body.message ?? body.detail;
  return typeof message === "string" ? message : "Something went wrong";
};

export const CDLRepositoryFactory = ({
  APIProvider,
  APIProvider2,
  AuthService,
  APIRoutes,
  timeProvider,
}: {
  APIProvider: IAPIProvider;
  APIProvider2: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
  timeProvider: ITimeProvider;
}) => {
  const getPatientDataFromCDL: ICDLRepository["getPatientDataFromCDL"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    const { ...params } = payload;
    const { GET } = APIProvider2;
    const { getAuthenticationAccessToken } = AuthService;

    const apiResponse = (await GET(
      APIRoutes.apiPatientGraphDataV2,
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: rrCDLFields,
        patient_ids: [patientId],
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL };

    if (apiResponse) {
      // Check response type

      const { timezone } = apiResponse[patientId];
      const processedData = await processCDLData(apiResponse[patientId]);
      const processedHourlyData = formatHourly3DData(
        processedData,
        "rr_3s.ds_hrly",
        "rr_3s",
        timezone
      );

      const processedDailyData = formatDaily3DData(
        processedData,
        "rr_3s.ds_dly",
        "rr_3s",
        timezone
      );
      const categorizedData: AwaitableReturn<getPatientDataFromCDL>["data"] = {
        "1D": [],
        "3D": [],
        "5D": [],
        "30D": [],
        dailyExtra: [],
        hourlyExtra: [],
      };

      const thirtyDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(31, "days");
      const thirtySevenDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(37, "days");
      processedDailyData.forEach((item) => {
        // Convert the item's timestamp into a date/time object using the appropriate library method
        const date = timeProvider.dateLibrary(item.dt);

        // Check if the item's date is after the threshold
        if (date.isAfter(thirtyDaysAgo)) {
          categorizedData["30D"].push(item);
        }
        if (date.isAfter(thirtySevenDaysAgo)) {
          categorizedData["dailyExtra"].push(item);
        }
      });
      processedHourlyData.forEach((item) => {
        const date = timeProvider.dateLibrary.tz(item.dt, timezone);
        // Anchor the hourly buckets to the *current* facility time so they
        // match the chart's x-axis window (which uses getFacilityTime). Using
        // params.date_to (end-of-day 23:59:59) here left an empty band at the
        // start of the hourly charts equal to the hours remaining in the day —
        // visible on the 3D window, negligible on the wider 5D window.
        const now = timeProvider.getFacilityTime(timezone);

        // Last 24 hours
        if (date.isAfter(now.clone().subtract(25, "hours"))) {
          categorizedData["1D"].push(item);
        }
        // Last 72 hours
        if (date.isAfter(now.clone().subtract(73, "hours"))) {
          categorizedData["3D"].push(item);
        }
        // Last 120 hours (5 days)
        if (date.isAfter(now.clone().subtract(121, "hours"))) {
          categorizedData["5D"].push(item);
        }
        if (date.isAfter(now.clone().subtract(145, "hours"))) {
          categorizedData["hourlyExtra"].push(item);
        }
      });
      return {
        success: true,
        data: categorizedData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  // Daily RR over an arbitrary date range.
  //
  // Hits the same V2 batch endpoint as the patient profile
  // (`/v2/circadia-vitals/patient_processed/` with `patient_ids: [...]`),
  // but via `APIProvider` (= CDL prod gateway, `cdl.circadia.link`) — that
  // is where the live patient data lives, not the dev gateway.
  //
  // Example URL produced:
  //   https://cdl.circadia.link/v2/circadia-vitals/patient_processed/
  //     ?date_from=2026-04-01T00:00:00-07:00
  //     &date_to=2026-04-30T23:59:59-07:00
  //     &db=3s
  //     &fields=rr_3s.ds_dly
  //     &patient_ids=113676
  //
  // Date strings are expected to be full ISO 8601 *with timezone offset*
  // (not `Z`); see `MonthlyReportModal/useHook.ts` for how they are built.
  const getPatientDailyRRData: ICDLRepository["getPatientDailyRRData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    const { ...params } = payload;
    const { GET } = APIProvider;
    const { getAuthenticationAccessToken } = AuthService;

    const apiResponse = (await GET(
      APIRoutes.apiPatientGraphDataV2,
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: "rr_3s.ds_dly",
        patient_ids: [patientId],
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL } | undefined;

    const perPatient = apiResponse?.[patientId];
    if (perPatient) {
      const processedData = await processCDLData(perPatient);
      const processedDailyData = formatDailyData(
        processedData,
        "rr_3s.ds_dly",
        "rr_3s",
        perPatient.timezone
      );
      return {
        success: true,
        data: processedDailyData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  // Daily HR — mirror of `getPatientDailyRRData`. Same V2 batch endpoint
  // + `APIProvider` (CDL prod) setup; only `db` and `fields` differ.
  //
  // Kept separate from `getHeartRateData` because that method categorises
  // the response into fixed 1D/3D/5D/30D buckets relative to `date_to`,
  // which doesn't fit the billing report's full-month window.
  const getPatientDailyHRData: ICDLRepository["getPatientDailyHRData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    const { ...params } = payload;
    const { GET } = APIProvider;
    const { getAuthenticationAccessToken } = AuthService;

    const apiResponse = (await GET(
      APIRoutes.apiPatientGraphDataV2,
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "HR3s",
        fields: "hr_3s.ds_dly",
        patient_ids: [patientId],
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL } | undefined;

    const perPatient = apiResponse?.[patientId];
    if (perPatient) {
      const processedData = await processCDLData(perPatient);
      const processedDailyData = formatDailyData(
        processedData,
        "hr_3s.ds_dly",
        "hr_3s",
        perPatient.timezone
      );
      return {
        success: true,
        data: processedDailyData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  // ── Hourly RR / HR for an arbitrary date range ─────────────────────────────
  //
  // Mirror of `getPatientDailyRRData` / `getPatientDailyHRData` but pulls
  // `rr_3s.ds_hrly` / `hr_3s.ds_hrly` so the billing Monthly Report can
  // render an hourly trend chart over the full billing month.  Each call
  // returns ~24×N processed points for an N-day range.
  const getPatientHourlyRRData: ICDLRepository["getPatientHourlyRRData"] =
    async (payload) => {
      const { patient_id: patientId, ...params } = payload;
      const { GET } = APIProvider;
      const { getAuthenticationAccessToken } = AuthService;

      const apiResponse = (await GET(
        APIRoutes.apiPatientGraphDataV2,
        {
          date_from: params.date_from,
          date_to: params.date_to,
          db: "3s",
          fields: "rr_3s.ds_hrly",
          patient_ids: [patientId],
        },
        {
          Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
        }
      )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL } | undefined;

      const perPatient = apiResponse?.[patientId];
      if (perPatient) {
        const processedData = await processCDLData(perPatient);
        const processedHourlyData = formatHourly3DData(
          processedData,
          "rr_3s.ds_hrly",
          "rr_3s",
          perPatient.timezone
        );
        return { success: true, data: processedHourlyData };
      }
      return { success: false, error: { message: "Something went wrong" } };
    };

  const getPatientHourlyHRData: ICDLRepository["getPatientHourlyHRData"] =
    async (payload) => {
      const { patient_id: patientId, ...params } = payload;
      const { GET } = APIProvider;
      const { getAuthenticationAccessToken } = AuthService;

      const apiResponse = (await GET(
        APIRoutes.apiPatientGraphDataV2,
        {
          date_from: params.date_from,
          date_to: params.date_to,
          db: "HR3s",
          fields: "hr_3s.ds_hrly",
          patient_ids: [patientId],
        },
        {
          Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
        }
      )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL } | undefined;

      const perPatient = apiResponse?.[patientId];
      if (perPatient) {
        const processedData = await processCDLData(perPatient);
        const processedHourlyData = formatHourly3DData(
          processedData,
          "hr_3s.ds_hrly",
          "hr_3s",
          perPatient.timezone
        );
        return { success: true, data: processedHourlyData };
      }
      return { success: false, error: { message: "Something went wrong" } };
    };

  // ── Per-minute RR / HR for the gated Patient Profile "1D" view ─────────────
  //
  // Mirrors the hourly variants but pulls `rr_3s.ds_min` / `hr_3s.ds_min` and
  // uses `APIProvider2` (same CDL dev gateway as the patient-profile RR/HR
  // charts) so the minutely data is consistent with the hourly data already
  // shown. `formatHourly3DData` already formats to `YYYY-MM-DD HH:mm:00`, so it
  // preserves the per-minute timestamps without a dedicated formatter.
  const getPatientMinutelyRRData: ICDLRepository["getPatientMinutelyRRData"] =
    async (payload) => {
      const { patient_id: patientId, ...params } = payload;
      const { GET } = APIProvider2;
      const { getAuthenticationAccessToken } = AuthService;

      const apiResponse = (await GET(
        APIRoutes.apiPatientGraphDataV2,
        {
          date_from: params.date_from,
          date_to: params.date_to,
          db: "3s",
          fields: rrMinutelyField,
          patient_ids: [patientId],
        },
        {
          Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
        }
      )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL } | undefined;

      const perPatient = apiResponse?.[patientId];
      if (perPatient) {
        const processedData = await processCDLData(perPatient);
        const processedMinutelyData = formatHourly3DData(
          processedData,
          rrMinutelyField,
          "rr_3s",
          perPatient.timezone
        );
        return { success: true, data: processedMinutelyData };
      }
      return { success: false, error: { message: "Something went wrong" } };
    };

  const getPatientMinutelyHRData: ICDLRepository["getPatientMinutelyHRData"] =
    async (payload) => {
      const { patient_id: patientId, ...params } = payload;
      const { GET } = APIProvider2;
      const { getAuthenticationAccessToken } = AuthService;

      const apiResponse = (await GET(
        APIRoutes.apiPatientGraphDataV2,
        {
          date_from: params.date_from,
          date_to: params.date_to,
          db: "HR3s",
          fields: hrMinutelyField,
          patient_ids: [patientId],
        },
        {
          Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
        }
      )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL } | undefined;

      const perPatient = apiResponse?.[patientId];
      if (perPatient) {
        const processedData = await processCDLData(perPatient);
        const processedMinutelyData = formatHourly3DData(
          processedData,
          hrMinutelyField,
          "hr_3s",
          perPatient.timezone
        );
        return { success: true, data: processedMinutelyData };
      }
      return { success: false, error: { message: "Something went wrong" } };
    };

  // Multi-patient RR / HR for the list pages (Inbox, Clinical, Watchlist,
  // Search, Trends). One V2 request against the CDL dev gateway; patients
  // missing from the response are omitted from `data`.
  const getPatientsVitalsBatch: ICDLRepository["getPatientsVitalsBatch"] =
    async ({
      patient_ids: patientIds,
      metric,
      granularity,
      signal,
      ...params
    }) => {
      const { GET } = APIProvider2;
      const { getAuthenticationAccessToken } = AuthService;
      const { db, field } = vitalsBatchQuery[metric][granularity];
      const extractKey = `${metric}_3s`;
      const formatData =
        granularity === "hourly" ? formatHourly3DData : formatDailyData;

      try {
        const apiResponse = (await GET(
          APIRoutes.apiPatientGraphDataV2,
          {
            date_from: params.date_from,
            date_to: params.date_to,
            db,
            fields: field,
            patient_ids: patientIds,
          },
          {
            Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
          },
          signal
        )) as Record<string, unknown> | undefined;

        if (!apiResponse || typeof apiResponse !== "object") {
          return { success: false, error: { message: "Something went wrong" } };
        }

        const isPatientPayload = (value: unknown) =>
          typeof value === "object" && value !== null;

        // APIProvider resolves error responses with the error body, e.g.
        // `{ message }` or `{ detail }`, rather than throwing.
        if (
          !patientIds.some((id) => isPatientPayload(apiResponse[id])) &&
          ("message" in apiResponse || "detail" in apiResponse)
        ) {
          return {
            success: false,
            error: { message: getErrorMessage(apiResponse) },
          };
        }

        const data: { [patientId: string]: IParsedCDLItem[] } = {};
        for (const patientId of patientIds) {
          const perPatient = apiResponse[patientId];
          if (!isPatientPayload(perPatient)) continue;

          const payload = perPatient as CDL_API_RESPONSE_INTERNAL;
          try {
            const processedData = await processCDLData(payload);
            data[patientId] = formatData(
              processedData,
              field,
              extractKey,
              payload.timezone
            );
          } catch (error) {
            console.error("[CDL] Failed to decode vitals batch payload", error);
          }
        }

        return { success: true, data };
      } catch (error: unknown) {
        if ((error as Error)?.name === "AbortError") {
          return { success: false, error: { message: "Request was aborted" } };
        }

        console.error("[CDL] Vitals batch request failed", error);
        return { success: false, error: { message: "Something went wrong" } };
      }
    };

  const getBedExitMotionData: ICDLRepository["getBedExitMotionData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    const { ...params } = payload;
    const { GET } = APIProvider;
    const { getAuthenticationAccessToken } = AuthService;

    const response = (await GET(
      APIRoutes.apiPatientGraphData.replace(
        "{patient_id}",
        patientId?.toString()
      ),
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: `${mvmtFields},${bedExitFields}`,
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as CDL_API_RESPONSE_INTERNAL;

    if (response.message !== "No Circadia data found for the patient") {
      const processedData = await processCDLData(response);
      const processedMotionData = formatMotion(
        processedData,
        mvmtFields,
        "motion_indicator_min"
      );

      const processedBedExitData = formatMotion(
        processedData,
        bedExitFields,
        "bed_exit_prob"
      );

      return {
        success: true,
        data: {
          bedExit: processedBedExitData,
          motion: processedMotionData,
        },
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  const getHeartRateData: ICDLRepository["getHeartRateData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    const { ...params } = payload;
    const { GET } = APIProvider2;
    const { getAuthenticationAccessToken } = AuthService;

    const apiResponse = (await GET(
      APIRoutes.apiPatientGraphDataV2,
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "HR3s",
        fields: heartRateFields,
        patient_ids: [patientId],
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL };

    if (apiResponse) {
      // Check response type

      const { timezone } = apiResponse[patientId];
      const processedData = await processCDLData(apiResponse[patientId]);

      const processedHourlyData = formatHourly3DData(
        processedData,
        "hr_3s.ds_hrly",
        "hr_3s",
        timezone
      );

      const processedDailyData = formatDaily3DData(
        processedData,
        "hr_3s.ds_dly",
        "hr_3s",
        timezone
      );

      const categorizedData: AwaitableReturn<getPatientDataFromCDL>["data"] = {
        "1D": [],
        "3D": [],
        "5D": [],
        "30D": [],
        dailyExtra: [],
        hourlyExtra: [],
      };

      const thirtyDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(31, "days");
      const thirtySevenDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(37, "days");
      processedDailyData.forEach((item) => {
        // Convert the item's timestamp into a date/time object using the appropriate library method
        const date = timeProvider.dateLibrary(item.dt);

        // Check if the item's date is after the threshold
        if (date.isAfter(thirtyDaysAgo)) {
          categorizedData["30D"].push(item);
        }
        if (date.isAfter(thirtySevenDaysAgo)) {
          categorizedData["dailyExtra"].push(item);
        }
      });
      processedHourlyData.forEach((item) => {
        const date = timeProvider.dateLibrary.tz(item.dt, timezone);
        // Anchor the hourly buckets to the *current* facility time so they
        // match the chart's x-axis window (which uses getFacilityTime). Using
        // params.date_to (end-of-day 23:59:59) here left an empty band at the
        // start of the hourly charts equal to the hours remaining in the day —
        // visible on the 3D window, negligible on the wider 5D window.
        const now = timeProvider.getFacilityTime(timezone);

        // Last 24 hours
        if (date.isAfter(now.clone().subtract(25, "hours"))) {
          categorizedData["1D"].push(item);
        }
        // Last 72 hours
        if (date.isAfter(now.clone().subtract(73, "hours"))) {
          categorizedData["3D"].push(item);
        }
        // Last 120 hours (5 days)
        if (date.isAfter(now.clone().subtract(121, "hours"))) {
          categorizedData["5D"].push(item);
        }
        if (date.isAfter(now.clone().subtract(145, "hours"))) {
          categorizedData["hourlyExtra"].push(item);
        }
      });
      return {
        success: true,
        data: categorizedData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  return {
    getPatientDataFromCDL,
    getBedExitMotionData,
    getHeartRateData,
    getPatientDailyRRData,
    getPatientDailyHRData,
    getPatientHourlyRRData,
    getPatientHourlyHRData,
    getPatientMinutelyRRData,
    getPatientMinutelyHRData,
    getPatientsVitalsBatch,
  };
};
